import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { DynamicTextInfo, removeMustache } from "@/components/canvas/utils/canvasUtils";
import { SplitContentDynamicTemplateDataSourceResponseItem } from "../contentSplit/splitContent/splitContentTypes";
import { useTranslation } from "react-i18next";

const createFormSchema = (dynamicTexts: DynamicTextInfo[]) => {
  const schemaObj: Record<string, z.ZodString> = {};
  dynamicTexts.forEach((textObj) => {
    textObj.dynamicValues.forEach((value) => {
      schemaObj[`${textObj.indexInCanvasData}-${value}`] = z.string().min(1, { message: "Required" });
    });
  });
  return z.object(schemaObj);
};

interface Props {
  dynamicTexts: DynamicTextInfo[];
  onFormChange: (dataSource: SplitContentDynamicTemplateDataSourceResponseItem[]) => void;
}

export const CreateFromTemplateDynamicForm: React.FC<Props> = ({ dynamicTexts, onFormChange }) => {
  const { t } = useTranslation();
  const formSchema = createFormSchema(dynamicTexts);
  type FormSchemaType = z.infer<typeof formSchema>;

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
  });

  const handleValueChange = () => {
    const formValues = form.getValues();
    const dataSource: SplitContentDynamicTemplateDataSourceResponseItem[] = Object.entries(formValues).map(
      ([key, value]) => ({
        key: removeMustache(key),
        value: value || "",
      })
    );
    onFormChange(dataSource);
  };

  return (
    <Form {...form}>
      <form className="grid grid-cols-2 gap-2">
        {dynamicTexts.map((textObj) => (
          <div key={textObj.indexInCanvasData} className="border p-2 rounded-md mb-2">
            {textObj.dynamicValues.map((dynamicValue) => (
              <FormField
                key={dynamicValue}
                control={form.control}
                name={dynamicValue}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="w-[120px]">{dynamicValue}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("content.createFromTemplateDynamicFormPlaceholder")}
                        onChange={(e) => {
                          field.onChange(e);
                          handleValueChange();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        ))}
      </form>
    </Form>
  );
};
